/**
 * Shared procedural terrain (Phase A — multi-biome Commons) — the CLIENT mirror
 * of `server/src/lib/terrain.ts`.
 *
 * The heightfield is a pure deterministic function of the world's `terrainSeed`,
 * so the client renders the EXACT terrain the server gates placement against. Keep
 * the biome field math + `terrainHeight` VERBATIM with the server mirror.
 *
 * The old `terrainHeight(seed,x,z,biome)` signature is REMOVED; `terrainHeight`
 * now blends all six biomes internally — no `biome` argument needed at call sites.
 * The old `biomeAtRegions` region-table lookup was also removed (zero callers after
 * Phase-A migration); use `biomeWeights` / `biomeAt` directly instead.
 */

import { CITY_PIN_RADIUS, CITY_PIN_FALLOFF, BIOME_WAVELENGTH, LANDFORM_TERRAIN_SEEDS } from './constants.js';
import { settlementSites, type SettlementTier } from './settlements.js';
import { caveSites, CAVE_APRON_R, CAVE_APRON_RING, type CaveSite } from './caves.js';

/** Region edge (m). `regionOf(x) = floor(x / this)`. */
export const REGION_SIZE_M = 256;

// ── Optional native kernel (client only; the server mirror has none) ─────────────
// Phase 1 of the Wasm assessment: an AssemblyScript port of THIS file's math
// (client/assembly/terrain_kernel.ts) can be installed at runtime. It must be
// BIT-IDENTICAL to the JS below (verify:terrain-kernel proves Object.is parity)
// — the JS stays the oracle and the fallback; the math below is untouched.
export interface TerrainKernelHook {
  /** `terrainHeight` (with the talus relaxation) for a seed. */
  height(seed: string, x: number, z: number): number;
  /** Soft biome weights in BIOMES order into `out` (6 doubles). */
  biomeWeights(seed: string, x: number, z: number, out: Float64Array): void;
}
let kernel_: TerrainKernelHook | null = null;
const kernelOut_ = new Float64Array(6);
/** Install (or remove with null) the native terrain kernel. */
export function setTerrainKernel(k: TerrainKernelHook | null): void { kernel_ = k; }
/** True while a native kernel is serving terrain queries. */
export function terrainKernelActive(): boolean { return kernel_ !== null; }

// ── Low-level noise primitives (verbatim in server mirror) ───────────────────────

// ── Perf: exact-output numeric FNV (identical hashes, zero string allocation) ────
// hash2 used to build `${seed}:${ix}:${iz}` PER CALL — millions of temp strings
// per chunk build (GC hitching while moving). The seed prefix hash is memoized
// and the integers are fed as their ASCII digit codes, producing the IDENTICAL
// FNV-1a stream (verified sample-exact), so terrain layout does not change.
const seedPrefixCache = new Map<string, number>();
function fnvInt(h: number, n: number): number {
  if (n < 0) { h = Math.imul(h ^ 45, 16777619) >>> 0; n = -n; }
  if (n >= 10) h = fnvInt(h, Math.floor(n / 10));
  return Math.imul(h ^ (48 + (n % 10)), 16777619) >>> 0;
}
function hash2(seed: string, ix: number, iz: number): number {
  let h = seedPrefixCache.get(seed);
  if (h === undefined) {
    h = 2166136261 >>> 0;
    for (let i = 0; i < seed.length; i++) h = Math.imul(h ^ seed.charCodeAt(i), 16777619) >>> 0;
    h = Math.imul(h ^ 58, 16777619) >>> 0; // ':'
    seedPrefixCache.set(seed, h);
  }
  h = fnvInt(h, ix);
  h = Math.imul(h ^ 58, 16777619) >>> 0; // ':'
  h = fnvInt(h, iz);
  // Avalanche finalizer (murmur3 fmix32). Raw FNV-1a gives the LAST-fed
  // byte only one multiply round, so 10 consecutive iz cells shared nearly
  // the same value (Δ≈0.004/digit) with a jump at each tens rollover —
  // every noise field showed axis-aligned ~10-cell bands ("long blocky
  // hills"). NOTE: changing this re-rolls every field derived from a seed.
  h ^= h >>> 16;
  h = Math.imul(h, 0x85ebca6b) >>> 0;
  h ^= h >>> 13;
  h = Math.imul(h, 0xc2b2ae35) >>> 0;
  h ^= h >>> 16;
  return (h >>> 8) / 0xffffff;
}

/** Derived-seed strings ("<seed>:cont0" …) built ONCE per base seed — the call
 *  sites used template literals per invocation (more per-call allocation). */
const derivedSeedCache = new Map<string, Map<string, string>>();
function dseed(seed: string, suffix: string): string {
  let m = derivedSeedCache.get(seed);
  if (!m) { m = new Map(); derivedSeedCache.set(seed, m); }
  let v = m.get(suffix);
  if (v === undefined) { v = seed + ':' + suffix; m.set(suffix, v); }
  return v;
}


function smooth(t: number): number {
  return t * t * (3 - 2 * t);
}

/**
 * valueNoise + analytic ∂/∂(lattice x, z) — the primitive behind erodedFbm
 * (Inigo Quilez's derivative trick). Derivatives are in LATTICE units so the
 * damping term is wavelength-independent. VERBATIM in both mirrors.
 */
function valueNoiseD(seed: string, x: number, z: number, wavelength: number): [number, number, number] {
  const fx = x / wavelength;
  const fz = z / wavelength;
  const ix = Math.floor(fx);
  const iz = Math.floor(fz);
  const tx = fx - ix;
  const tz = fz - iz;
  const ux = tx * tx * (3 - 2 * tx);
  const uz = tz * tz * (3 - 2 * tz);
  const dux = 6 * tx * (1 - tx);
  const duz = 6 * tz * (1 - tz);
  const a = hash2(seed, ix, iz);
  const b = hash2(seed, ix + 1, iz);
  const c = hash2(seed, ix, iz + 1);
  const d = hash2(seed, ix + 1, iz + 1);
  const k1 = b - a;
  const k2 = c - a;
  const k4 = a - b - c + d;
  return [
    (a + k1 * ux + k2 * uz + k4 * ux * uz) * 2 - 1,
    2 * dux * (k1 + k4 * uz),
    2 * duz * (k2 + k4 * ux),
  ];
}

/**
 * IQ derivative-accumulated fBm (±~1): each octave is damped by the slopes
 * accumulated so far — ridge tops stay smooth while valleys collect detail,
 * the analytic "erosion look" (no simulation, works on an infinite field).
 * ~37° inter-octave rotation kills axis-aligned lattice artifacts.
 */
function erodedFbm(seed: string, x: number, z: number, wavelength: number, octaves: number): number {
  let sum = 0;
  let amp = 1;
  let norm = 0;
  let dx = 0;
  let dz = 0;
  let cx = x;
  let cz = z;
  let wl = wavelength;
  for (let o = 0; o < octaves; o++) {
    const [v, ndx, ndz] = valueNoiseD(dseed(seed, 'er' + o), cx, cz, wl);
    // Half-weight accumulation: cubic value-noise derivatives run ~3× hotter
    // than the gradient noise IQ's formula assumes — full weight crushes
    // every octave past the first (billowy mush instead of eroded detail).
    dx += ndx * 0.5;
    dz += ndz * 0.5;
    sum += (amp * v) / (1 + dx * dx + dz * dz);
    norm += amp;
    amp *= 0.55;
    const rx = 0.8 * cx - 0.6 * cz;
    const rz = 0.6 * cx + 0.8 * cz;
    cx = rx;
    cz = rz;
    wl /= 2;
  }
  return sum / norm;
}

function valueNoise(seed: string, x: number, z: number, wavelength: number): number {
  const fx = x / wavelength;
  const fz = z / wavelength;
  const ix = Math.floor(fx);
  const iz = Math.floor(fz);
  const tx = smooth(fx - ix);
  const tz = smooth(fz - iz);
  const n00 = hash2(seed, ix, iz);
  const n10 = hash2(seed, ix + 1, iz);
  const n01 = hash2(seed, ix, iz + 1);
  const n11 = hash2(seed, ix + 1, iz + 1);
  const a = n00 + (n10 - n00) * tx;
  const b = n01 + (n11 - n01) * tx;
  return a + (b - a) * tz;
}

// ── Biome field ──────────────────────────────────────────────────────────────────

export type Biome =
  | 'regolith_plains' | 'crystal_forest' | 'dune_desert'
  | 'rocky_highlands' | 'moss_wetland' | 'fungal_basin';

export const BIOMES: readonly Biome[] = [
  'regolith_plains', 'crystal_forest', 'dune_desert', 'rocky_highlands', 'moss_wetland', 'fungal_basin',
] as const;

function clamp01(v: number): number { return v < 0 ? 0 : v > 1 ? 1 : v; }

/** Whittaker centers (temp, moist, rugged) per biome + softness — all TUNABLE.
 *  No longer exported: the per-vertex attribute approach (Phase-A ground shader) bakes biome
 *  weights on the CPU so the shader reads pre-computed attributes; TSL no longer needs these. */
const BIOME_SOFT = 0.22;
const BIOME_CENTER: Record<Biome, [number, number, number]> = {
  regolith_plains: [0.50, 0.40, 0.30],
  crystal_forest:  [0.45, 0.62, 0.38],
  dune_desert:     [0.82, 0.15, 0.28],
  rocky_highlands: [0.42, 0.35, 0.85],
  moss_wetland:    [0.32, 0.86, 0.20],
  fungal_basin:    [0.62, 0.72, 0.26],
};

/** Soft biome membership (sums to 1). Two ~900 m climate fields + a ruggedness field,
 *  gaussian-scored, normalized, then collapsed to plains near the city. */
export function biomeWeights(seed: string, x: number, z: number): Record<Biome, number> {
  if (kernel_) {
    kernel_.biomeWeights(seed, x, z, kernelOut_);
    return {
      regolith_plains: kernelOut_[0], crystal_forest: kernelOut_[1], dune_desert: kernelOut_[2],
      rocky_highlands: kernelOut_[3], moss_wetland: kernelOut_[4], fungal_basin: kernelOut_[5],
    };
  }
  const temp = valueNoise(dseed(seed, 'temp'), x, z, BIOME_WAVELENGTH);
  const moist = valueNoise(dseed(seed, 'moist'), x, z, BIOME_WAVELENGTH);
  const rugged = valueNoise(dseed(seed, 'rug'), x, z, BIOME_WAVELENGTH * 0.7);
  const raw = {} as Record<Biome, number>;
  let total = 0;
  for (const b of BIOMES) {
    const [ct, cm, cr] = BIOME_CENTER[b];
    const dt = temp - ct, dm = moist - cm, dr = rugged - cr;
    const s = Math.exp(-(dt * dt + dm * dm + dr * dr) / (2 * BIOME_SOFT * BIOME_SOFT));
    raw[b] = s; total += s;
  }
  const w = {} as Record<Biome, number>;
  for (const b of BIOMES) w[b] = raw[b] / total;
  // City pin → blend toward 100% plains within the radius.
  const d = Math.hypot(x, z);
  const city = 1 - smooth(clamp01((d - CITY_PIN_RADIUS) / (CITY_PIN_FALLOFF - CITY_PIN_RADIUS)));
  if (city > 0) {
    for (const b of BIOMES) w[b] *= (1 - city);
    w.regolith_plains += city;
  }
  return w;
}

export function biomeAt(seed: string, x: number, z: number): Biome {
  const w = biomeWeights(seed, x, z);
  let best: Biome = 'regolith_plains', bv = -1;
  for (const b of BIOMES) if (w[b] > bv) { bv = w[b]; best = b; }
  return best;
}

// ── Per-biome height profiles ────────────────────────────────────────────────────

/** Per-biome height profile (m). All tunable; shapes match the spec table. */
function profile(seed: string, x: number, z: number, b: Biome): number {
  const gentle = (valueNoise(seed, x, z, 90) - 0.5) * 2 * 4 + (valueNoise(dseed(seed, 'o'), x, z, 30) - 0.5) * 2;
  switch (b) {
    // Was a SINGLE-OCTAVE ±60 m @ 18 m value noise — one-octave lattice noise
    // reads as axis-aligned rectangular mounds (the "weird long blocky hills").
    // Rotated derivative-eroded multi-octave relief + a small texture octave.
    case 'rocky_highlands':
      return gentle
        + erodedFbm(dseed(seed, 'rk'), x, z, 700, 4) * 42
        + (valueNoise(dseed(seed, 'm'), x, z, 30) - 0.5) * 2 * 7;
    case 'dune_desert': {
      // Seed-rotated, warped dune crests — the old axis-aligned sin(x)
      // stripes read as artificial long parallel hills from any height.
      const th = hash2(dseed(seed, 'dang'), 0, 0) * Math.PI;
      const u = x * Math.cos(th) + z * Math.sin(th);
      const dw = (valueNoise(dseed(seed, 'dwarp'), x, z, 260) - 0.5) * 2 * 55;
      return gentle + Math.sin((u + dw) / 40) * 6 + (valueNoise(dseed(seed, 'd'), x, z, 60) - 0.5) * 2 * 5;
    }
    case 'moss_wetland':    return gentle * 0.4 - 3;
    case 'fungal_basin':    return gentle + (valueNoise(dseed(seed, 'f'), x, z, 140) - 0.5) * 2 * 8;
    default:                return gentle; // regolith_plains, crystal_forest
  }
}


// ── Macro relief (v2 — continental swells + domain-warped ridged mountains) ──────
// Flattened to zero inside the city pin so every pre-existing build (old ±512
// world ⊂ pin) keeps its ground. Pure/deterministic; VERBATIM in both mirrors.
function pinMask(x: number, z: number): number {
  const d = Math.hypot(x, z);
  if (d <= CITY_PIN_RADIUS) return 1;
  if (d >= CITY_PIN_FALLOFF) return 0;
  const t = (d - CITY_PIN_RADIUS) / (CITY_PIN_FALLOFF - CITY_PIN_RADIUS);
  return 1 - t * t * (3 - 2 * t);
}

function macroHeight(seed: string, x: number, z: number): number {
  // Continental swells: 3-octave fBm, ~220 km base wavelength, ±320 m.
  let c = 0, amp = 1, wl = 220000;
  for (let o = 0; o < 3; o++) {
    c += (valueNoise(dseed(seed, 'cont' + o), x, z, wl) - 0.5) * 2 * amp;
    wl /= 2.1; amp /= 2;
  }
  const continental = c * 320;
  // Domain-warped ridged multifractal mountains (34 km ridges) masked by a
  // 180 km range field so mountains form BELTS, not everywhere. Terrain
  // realism: a SECOND 2.8 km warp octave + ~37° per-octave rotation break the
  // single-warp value-noise crests that read as long parallel walls, and a
  // 21 km segment field splits continuous ridgelines into massifs.
  const wx = (valueNoise(dseed(seed, 'wx'), x, z, 60000) - 0.5) * 16000
           + (valueNoise(dseed(seed, 'wx2'), x, z, 9000) - 0.5) * 2800;
  const wz = (valueNoise(dseed(seed, 'wz'), x, z, 60000) - 0.5) * 16000
           + (valueNoise(dseed(seed, 'wz2'), x, z, 9000) - 0.5) * 2800;
  let r = 0; amp = 1; wl = 34000;
  let rx = x + wx, rz = z + wz;
  for (let o = 0; o < 4; o++) {
    const n = valueNoise(dseed(seed, 'ridge' + o), rx, rz, wl);
    const sIn = 1 - Math.abs(2 * n - 1);
    r += sIn * sIn * amp;
    const nx = 0.8 * rx - 0.6 * rz;
    const nz = 0.6 * rx + 0.8 * rz;
    rx = nx; rz = nz;
    wl /= 2; amp /= 2;
  }
  const seg = 0.55 + 0.45 * valueNoise(dseed(seed, 'rseg'), x, z, 21000);
  const mountains = (r / 1.875 - 0.35) * 300 * seg;
  const range = clamp01((valueNoise(dseed(seed, 'mmask'), x, z, 180000) - 0.42) * 2.2);
  return continental + Math.max(0, mountains) * range;
}

/** Blended terrain height (m) — Σ biomeWeight·profile. Continuous across borders.
 *  Deterministic; shared with the server. No `biome` argument — biome is internal. */
function terrainHeightRaw(seed: string, x: number, z: number): number {
  const w = biomeWeights(seed, x, z);
  const open = 1 - pinMask(x, z); // 0 inside the city pin — old builds keep their ground
  let h = macroHeight(seed, x, z) * open; // v2 relief, pin-safe
  // Meso erosion band (terrain realism): IQ derivative-accumulated fBm fills
  // the 6 km → 375 m spectral GAP between the macro belts and the ≤90 m biome
  // micro-detail — the gap is exactly what made hills read as long smooth
  // walls. Slope-damped octaves keep ridge tops smooth while valleys collect
  // detail; rugged biomes get more of it.
  const rugged = w.rocky_highlands + 0.4 * w.crystal_forest + 0.25 * w.fungal_basin;
  h += erodedFbm(seed, x, z, 6000, 5) * 45 * (0.55 + 0.45 * rugged) * open;
  for (const b of BIOMES) if (w[b] > 1e-4) h += w[b] * profile(seed, x, z, b);
  return h;
}


/**
 * Single-pass thermal relaxation (talus smoothing) — the reference's GPU-friendly
 * erosion approximation. Each point relaxes toward its 4-neighbour average in
 * proportion to how far the local slope exceeds the talus angle, sampling
 * neighbours through the PURE raw height function, so the result stays
 * deterministic AND seamless across chunk borders (every chunk computes the
 * identical value for a shared point). Rounds ridge crests + softens scree
 * slopes without moving gentle terrain at all.
 */
const TALUS_TAN = 0.55;   // ~29° — material rests below this
const RELAX_STEP = 6;     // neighbour sampling distance (m)
const RELAX_MAX = 0.5;    // max blend toward the neighbour average

/** The BASE field: raw height + talus relax, before the landform layer.
 *  Site pickers sample THIS (the plateaus depend on the sites). */
export function terrainHeightBase(seed: string, x: number, z: number): number {
  if (kernel_) return kernel_.height(seed, x, z);
  const h = terrainHeightRaw(seed, x, z);
  const hL = terrainHeightRaw(seed, x - RELAX_STEP, z);
  const hR = terrainHeightRaw(seed, x + RELAX_STEP, z);
  const hD = terrainHeightRaw(seed, x, z - RELAX_STEP);
  const hU = terrainHeightRaw(seed, x, z + RELAX_STEP);
  const gx = (hR - hL) / (2 * RELAX_STEP);
  const gz = (hU - hD) / (2 * RELAX_STEP);
  const slope = Math.sqrt(gx * gx + gz * gz);
  if (slope <= TALUS_TAN) return h;
  const t = Math.min(1, (slope - TALUS_TAN) / TALUS_TAN);
  const k = RELAX_MAX * t * t * (3 - 2 * t);
  const avg = (hL + hR + hD + hU) * 0.25;
  return h + (avg - h) * k;
}

// ── Landforms (2026-09-08 terrain wave) — post-operators over the BASE field ────
//
// Everything in this section runs in JS AFTER the base height (kernel or JS,
// bit-identical) and is written VERBATIM in shared/terrain.ts and
// server/src/lib/terrain.ts. The AssemblyScript kernel never sees it: the
// chunk worker applies `landformHeight` to every kernel-filled vertex exactly
// as `terrainHeight` does per point, so parity is by construction
// (verify:terrain-kernel pins the base, verify:plateaus pins the layer).
//
// Settlement PLATEAUS ("the terrain is flat under every settlement so the
// structures are placed evenly"): each site becomes a level disc at the mean
// base height of its footprint (half cut, half fill — the shortest ramps),
// blended back into the field over a ring that widens with the local height
// difference so the ramp never exceeds ~32°. Roads/pads then grade to a flat
// ground and every foundation in a plan lands at one height.
//
// Composition law (order-independent, continuous): discs are kept DISJOINT
// (pairwise shrink with an 80 m gap), a point's ring weights are combined as a
// normalized blend under a union weight, and every other site's disc SUPPRESSES
// a ring in a 60 m exclusion band outside its rim — so at a rim only the disc's
// own weight survives (= its level, exactly) and no order of evaluation can
// leave a step where two rings meet.

/** Flat disc radius (m) per tier — covers the DESIRED plan radius (420/180/95)
 *  plus the stamp margin, so every pad, road and yard of a plan stands level. */
export const PLATEAU_RADIUS: Record<SettlementTier, number> = { city: 440, town: 200, hamlet: 115, capital: 3380, district: 0, circuit: 500 };
/** Minimum blend ring (m) beyond the disc. */
const PLATEAU_RING_MIN: Record<SettlementTier, number> = { city: 160, town: 90, hamlet: 60, capital: 200, district: 0, circuit: 120 };
/** Ring width per metre of height difference: 1.6 ⇒ a ramp no steeper than ~32°. */
const PLATEAU_RAMP = 1.6;
/** Ring cap (m) — also the spatial-hash reach beyond the disc. */
const PLATEAU_RING_MAX = 480;
/** Minimum gap (m) kept between two plateau discs (pairwise shrink) — MUST exceed
 *  PLATEAU_EXCLUDE so no disc ever lies inside another's exclusion band. */
const PLATEAU_GAP = 80;
/** Band (m) outside a disc rim in which OTHER sites' rings fade to zero. */
const PLATEAU_EXCLUDE = 60;
/** Spatial-hash cell (m) for the landform index. */
const LANDFORM_CELL = 1024;

export interface PlateauSite { id: number; x: number; z: number; r: number; ring: number; h: number }
interface LandformIndex { grid: Map<number, PlateauSite[]>; plateaus: PlateauSite[] }
const landformCache = new Map<string, LandformIndex>();

function landformCellKey(ix: number, iz: number): number {
  return (ix + 4096) * 8192 + (iz + 4096);
}

function smooth01(t: number): number {
  return t <= 0 ? 0 : t >= 1 ? 1 : t * t * (3 - 2 * t);
}

/** The landform index for a seed (null when the seed carries no landforms). */
function landformIndex(seed: string): LandformIndex | null {
  if (!LANDFORM_TERRAIN_SEEDS.has(seed)) return null;
  let idx = landformCache.get(seed);
  if (idx) return idx;
  idx = { grid: new Map(), plateaus: [] };
  // Districts (capital wave) stand on their capital's disc — a zero-radius tier
  // carries no plateau of its own and must not enter the pairwise shrink.
  const sites = settlementSites(seed).filter((s) => PLATEAU_RADIUS[s.tier] > 0);
  const radii = sites.map((s) => PLATEAU_RADIUS[s.tier]);
  // Pairwise shrink (id order) so no two discs come within PLATEAU_GAP: radii
  // only ever decrease, so one pass satisfies every pair.
  for (let i = 0; i < sites.length; i++) {
    for (let j = i + 1; j < sites.length; j++) {
      const dx = sites[j].centerX - sites[i].centerX;
      const dz = sites[j].centerZ - sites[i].centerZ;
      const d = Math.sqrt(dx * dx + dz * dz);
      const need = radii[i] + radii[j] + PLATEAU_GAP;
      if (d >= need) continue;
      const k = Math.max(0, d - PLATEAU_GAP) / (radii[i] + radii[j]);
      radii[i] *= k;
      radii[j] *= k;
    }
  }
  for (let i = 0; i < sites.length; i++) {
    const s = sites[i];
    const r = radii[i];
    // Plateau level = mean BASE height over the disc (center + two rings of 8).
    let sum = terrainHeightBase(seed, s.centerX, s.centerZ);
    for (const f of [0.5, 1]) {
      for (let k = 0; k < 8; k++) {
        const a = (k / 8) * Math.PI * 2;
        sum += terrainHeightBase(seed, s.centerX + Math.cos(a) * r * f, s.centerZ + Math.sin(a) * r * f);
      }
    }
    const site: PlateauSite = { id: s.id, x: s.centerX, z: s.centerZ, r, ring: PLATEAU_RING_MIN[s.tier], h: sum / 17 };
    idx.plateaus.push(site);
    const reach = r + PLATEAU_RING_MAX;
    const x0 = Math.floor((s.centerX - reach) / LANDFORM_CELL), x1 = Math.floor((s.centerX + reach) / LANDFORM_CELL);
    const z0 = Math.floor((s.centerZ - reach) / LANDFORM_CELL), z1 = Math.floor((s.centerZ + reach) / LANDFORM_CELL);
    for (let ix = x0; ix <= x1; ix++) {
      for (let iz = z0; iz <= z1; iz++) {
        const k = landformCellKey(ix, iz);
        let list = idx.grid.get(k);
        if (!list) { list = []; idx.grid.set(k, list); }
        list.push(site);
      }
    }
  }
  landformCache.set(seed, idx);
  return idx;
}

/** Every settlement plateau of a seed, in site-id order (harnesses, maps, tools). */
export function plateauSites(seed: string): readonly PlateauSite[] {
  return landformIndex(seed)?.plateaus ?? [];
}

/** The plateau of one site (null when the seed carries no landforms). */
export function plateauFor(seed: string, siteId: number): PlateauSite | null {
  const p = landformIndex(seed)?.plateaus;
  if (!p) return null;
  const s = p[siteId];
  return s && s.id === siteId ? s : (p.find((q) => q.id === siteId) ?? null);
}

// Per-call scratch (single-threaded; never held across calls).
const lfD = new Float64Array(64);
const lfW = new Float64Array(64);

// ── Terraces (terrain wave 2026-09-08) — stratified benches on high ground ──────
//
// Sedimentary strata read as flat treads and steep risers; the operator is the
// classic height quantisation with a smoothstep riser, masked to the mountain
// belts (by height) and modulated by a slow strata field so benches come and
// go across a range rather than covering it. Pure, cheap (one hash octave),
// VERBATIM in both mirrors — the server seats on the same benches. Runs on
// the BASE height before rivers (a river cuts through the benches — the
// gorge walls then show the strata) and before plateaus.

/** Tread height (m) of a bench. */
const TERRACE_STEP = 14;
/** Riser share of a step (0..1): the last 30 % of each step rises. */
const TERRACE_RISER = 0.3;
/** Terraces fade in between these heights (m) — mountain belts only. */
const TERRACE_H0 = 140;
const TERRACE_H1 = 260;
/** Strata field wavelength (m) and the band of it that carries terraces. */
const TERRACE_FIELD_WL = 5200;
const TERRACE_FIELD_LO = 0.56;
const TERRACE_FIELD_HI = 0.74;
/** Strongest blend toward the quantised profile. */
const TERRACE_MAX = 0.85;

function terraceProfile(h: number): number {
  const s = h / TERRACE_STEP;
  const f = s - Math.floor(s);
  const r = f <= 1 - TERRACE_RISER ? 0 : (f - (1 - TERRACE_RISER)) / TERRACE_RISER;
  return (Math.floor(s) + r * r * (3 - 2 * r)) * TERRACE_STEP;
}

/** The terrace layer over a base height. */
export function terraceHeight(seed: string, x: number, z: number, h: number): number {
  if (h <= TERRACE_H0) return h;
  const hm = h >= TERRACE_H1 ? 1 : smooth01((h - TERRACE_H0) / (TERRACE_H1 - TERRACE_H0));
  const field = valueNoise(dseed(seed, 'strata'), x, z, TERRACE_FIELD_WL);
  const fm = field <= TERRACE_FIELD_LO || field >= TERRACE_FIELD_HI ? 0
    : field < (TERRACE_FIELD_LO + TERRACE_FIELD_HI) / 2
      ? smooth01((field - TERRACE_FIELD_LO) / ((TERRACE_FIELD_HI - TERRACE_FIELD_LO) / 2))
      : smooth01((TERRACE_FIELD_HI - field) / ((TERRACE_FIELD_HI - TERRACE_FIELD_LO) / 2));
  const k = TERRACE_MAX * hm * fm;
  if (k <= 0) return h;
  return h + (terraceProfile(h) - h) * k;
}

// ── Cave aprons (terrain wave 2026-09-08) — a level step at every cave mouth ──
//
// The mouth of a cave is a small level disc cut into the hillside (the tunnel
// floor starts here); a blend ring returns to the slope. Sites come from
// caves.ts (pure, mirrored), so the server seats on the same apron. Runs
// after rivers and before plateaus (a cave never sits near a plateau anyway).

interface CaveApronIndex { grid: Map<number, CaveSite[]> }
const caveApronCache = new Map<string, CaveApronIndex>();
const CAVE_APRON_CELL = 512;

function caveApronIndex(seed: string): CaveApronIndex | null {
  if (!LANDFORM_TERRAIN_SEEDS.has(seed)) return null;
  let idx = caveApronCache.get(seed);
  if (idx) return idx;
  idx = { grid: new Map() };
  const reach = CAVE_APRON_R + CAVE_APRON_RING;
  for (const c of caveSites(seed)) {
    const x0 = Math.floor((c.x - reach) / CAVE_APRON_CELL), x1 = Math.floor((c.x + reach) / CAVE_APRON_CELL);
    const z0 = Math.floor((c.z - reach) / CAVE_APRON_CELL), z1 = Math.floor((c.z + reach) / CAVE_APRON_CELL);
    for (let ix = x0; ix <= x1; ix++) {
      for (let iz = z0; iz <= z1; iz++) {
        const k = landformCellKey(ix, iz);
        let list = idx.grid.get(k);
        if (!list) { list = []; idx.grid.set(k, list); }
        list.push(c);
      }
    }
  }
  caveApronCache.set(seed, idx);
  return idx;
}

/** The cave-apron layer over an already-carved height. */
export function caveApronHeight(seed: string, x: number, z: number, h: number): number {
  const idx = caveApronIndex(seed);
  if (!idx) return h;
  const list = idx.grid.get(landformCellKey(Math.floor(x / CAVE_APRON_CELL), Math.floor(z / CAVE_APRON_CELL)));
  if (!list) return h;
  for (let i = 0; i < list.length; i++) {
    const c = list[i];
    const dx = x - c.x, dz = z - c.z;
    const d = Math.sqrt(dx * dx + dz * dz);
    if (d >= CAVE_APRON_R + CAVE_APRON_RING) continue;
    if (d <= CAVE_APRON_R) return c.h;
    const t = (d - CAVE_APRON_R) / CAVE_APRON_RING;
    h += (c.h - h) * (1 - t * t * (3 - 2 * t));
  }
  return h;
}

/** The full landform layer over a BASE height: terraces, then rivers (a cut),
 *  then cave aprons, then plateaus last (a disc always wins). This is the ONLY difference between
 *  `terrainHeight` and `terrainHeightBase`; the chunk worker composes the
 *  kernel-filled base the same way. */
export function landformHeight(seed: string, x: number, z: number, base: number): number {
  return plateauHeight(seed, x, z, caveApronHeight(seed, x, z, riverHeight(seed, x, z, terraceHeight(seed, x, z, base))));
}

/** The settlement-plateau layer over an already-carved height. */
export function plateauHeight(seed: string, x: number, z: number, base: number): number {
  const idx = landformIndex(seed);
  if (!idx) return base;
  const list = idx.grid.get(landformCellKey(Math.floor(x / LANDFORM_CELL), Math.floor(z / LANDFORM_CELL)));
  if (!list) return base;
  const n = list.length < 64 ? list.length : 64;
  let any = false;
  for (let i = 0; i < n; i++) {
    const s = list[i];
    const dx = x - s.x;
    const dz = z - s.z;
    const d = Math.sqrt(dx * dx + dz * dz);
    lfD[i] = d;
    let w = 0;
    if (d <= s.r) w = 1;
    else if (d < s.r + PLATEAU_RING_MAX) {
      const ring = Math.min(PLATEAU_RING_MAX, Math.max(s.ring, Math.abs(s.h - base) * PLATEAU_RAMP));
      if (d < s.r + ring) w = 1 - smooth01((d - s.r) / ring);
    }
    lfW[i] = w;
    if (w > 0) any = true;
  }
  if (!any) return base;
  let sumW = 0;
  let sumWH = 0;
  let prodInv = 1;
  for (let i = 0; i < n; i++) {
    let w = lfW[i];
    if (w <= 0) continue;
    // Other discs suppress this RING in their exclusion band (and fully inside);
    // a disc weight (w = 1) is never suppressed — discs sit ≥ PLATEAU_GAP apart.
    for (let j = 0; w < 1 && j < n; j++) {
      if (j === i) continue;
      const t = list[j];
      const gap = lfD[j] - t.r;
      if (gap < PLATEAU_EXCLUDE) w *= smooth01(gap / PLATEAU_EXCLUDE);
    }
    if (w <= 0) continue;
    sumW += w;
    sumWH += w * list[i].h;
    prodInv *= 1 - w;
  }
  if (sumW <= 0) return base;
  const target = sumWH / sumW;
  if (prodInv === 0) return target;
  return base + (target - base) * (1 - prodInv);
}


// ── Rivers (terrain wave 2026-09-08) — the baked network carved at query time ──
//
// RIVERS ARE CLIENT-SIDE DATA. The network is extracted once per world by the
// hydrology bake (client/src/lib/hydrology_bake.ts → public/hydrology/) and
// registered here by the client (main thread AND every terrain worker). The
// server mirror carries this code verbatim but never registers a network: the
// bake keeps every channel ≥ 300 m clear of every plateau disc, and the
// carve runs BEFORE the plateau layer, so nothing the server seats (settlement
// pads, spawn pads, hinterland around a plateau) can sit in a channel.
// Nothing server-authoritative may depend on channel heights.
//
// Data: one flat array per river, RIVER_STRIDE numbers per point —
// x, z, water surface (m), channel width (m), channel depth (m), flags.
// Flag 1 = a waterfall lip lies on the segment starting at this point.
// Carve = per nearby segment a compactly supported cross-section (Peytavie
// et al. 2019 "Procedural Riverscapes": riverbeds inscribed by combining
// compactly supported elevation modifiers along the course); the channel is
// a parabola from (water − depth) at the centre to (water + FREEBOARD) at the
// bank, the bank shelf fades over one channel width; normal reaches only CUT
// (min), waterfall reaches SET the ledge so the upstream water stays held.
// Several segments compose by min — a union of channels, continuous by
// construction. Talus relax happened before; the plateau layer runs after.

export const RIVER_STRIDE = 6;
export const RIVER_FLAG_FALL = 1;
/** Flag 2: a reach lying ON a lake or at the sea (set at decode, never baked) — it
 *  cuts its channel but never raises ground: a river must not build a bar under
 *  another body of water (the Min-blend caveat of every water-body tool). */
export const RIVER_FLAG_NORAISE = 2;
/** Water sits this far below the natural bank (mirrors hydrology_bake FREEBOARD). */
const RIVER_FREEBOARD = 0.6;
/** Spatial-hash cell (m) of the segment index. */
const RIVER_CELL = 128;
/** Horizontal run (m) over which a waterfall's water surface drops. */
export const FALL_LIP_RUN = 4;
/** Reach beyond a segment's channel edge (bank shelf) = width·BANK_K, at least BANK_MIN. */
const RIVER_BANK_K = 0.9;
const RIVER_BANK_MIN = 6;
/** Flat bank top past the channel edge (m) — wider than the ribbon's 0.7 m lip, so
 *  the water's edge always meets ground ABOVE the water. */
const RIVER_BANK_LIP = 1.0;
/** Falloff support when the modifier RAISES ground (a hollow under the channel):
 *  wider than the cut shelf, so a river across a dip builds a gentle alluvial
 *  ridge rather than a wall (width·K, at least MIN). */
const RIVER_RAISE_K = 2.5;
const RIVER_RAISE_MIN = 18;

interface RiverNet {
  /** Segment table, RIVER_COLS doubles per segment (see the column constants). */
  seg: Float64Array;
  count: number;
  /** Sorted cell keys and CSR offsets into `cellSegs` (segment indices). */
  cellKeys: Float64Array;
  cellStart: Int32Array;
  cellSegs: Int32Array;
}
// Segment columns (doubles — coordinates reach ±1e6 m, beyond Float32 precision).
const C_AX = 0, C_AZ = 1, C_BX = 2, C_BZ = 3, C_WA = 4, C_WB = 5, C_DA = 6, C_DB = 7, C_PA = 8, C_PB = 9, C_FALL = 10, C_REACH = 11, C_LEN = 12, C_NORAISE = 13, C_RREACH = 14;
const RIVER_COLS = 15;
const riverCache = new Map<string, RiverNet>();

function riverCellKey(ix: number, iz: number): number {
  return (ix + 32768) * 65536 + (iz + 32768);
}

/** Binary search of a cell key → [start, end) into cellSegs, or null. */
function riverCellRange(net: RiverNet, key: number): [number, number] | null {
  let lo = 0, hi = net.cellKeys.length - 1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const k = net.cellKeys[mid];
    if (k === key) return [net.cellStart[mid], net.cellStart[mid + 1]];
    if (k < key) lo = mid + 1; else hi = mid - 1;
  }
  return null;
}

/**
 * Register (or clear) the river network of a seed. Each river is a flat array
 * of RIVER_STRIDE numbers per point. Idempotent; replaces any earlier network.
 *
 * Structure-of-arrays (perf frontier 2026-09-08): the first version built one
 * object per segment inside a Map of arrays — 95 MB of heap and ~190 ms of
 * indexing PER THREAD, five threads deep. This layout is ~14 MB and indexes
 * in a few tens of ms: a doubles table for the segments and a CSR spatial
 * index (sorted cell keys → segment index runs) looked up by binary search.
 */
export function setRiverNetwork(seed: string, rivers: ReadonlyArray<ArrayLike<number>> | null): void {
  if (!rivers) { riverCache.delete(seed); return; }
  let count = 0;
  for (const r of rivers) count += Math.max(0, Math.floor(r.length / RIVER_STRIDE) - 1);
  const seg = new Float64Array(count * RIVER_COLS);
  let s = 0;
  let cellEntries = 0;
  for (const r of rivers) {
    const m = Math.floor(r.length / RIVER_STRIDE);
    for (let k = 0; k < m - 1; k++) {
      const a = k * RIVER_STRIDE, b = a + RIVER_STRIDE;
      const o = s * RIVER_COLS;
      seg[o + C_AX] = r[a]; seg[o + C_AZ] = r[a + 1]; seg[o + C_BX] = r[b]; seg[o + C_BZ] = r[b + 1];
      seg[o + C_WA] = r[a + 2]; seg[o + C_WB] = r[b + 2]; seg[o + C_DA] = r[a + 3]; seg[o + C_DB] = r[b + 3];
      seg[o + C_PA] = r[a + 4]; seg[o + C_PB] = r[b + 4];
      seg[o + C_FALL] = (r[a + 5] & RIVER_FLAG_FALL) !== 0 ? 1 : 0;
      // Either end on still water ⇒ the whole reach is cut-only.
      const noRaise = ((r[a + 5] | r[b + 5]) & RIVER_FLAG_NORAISE) !== 0;
      seg[o + C_NORAISE] = noRaise ? 1 : 0;
      const wMax = Math.max(seg[o + C_DA], seg[o + C_DB]);
      const bankMax = Math.max(RIVER_BANK_MIN, wMax * RIVER_BANK_K);
      // C_REACH = the cut shelf's reach (riverNear: foliage keep-outs, bridges);
      // C_RREACH = the modifier's full reach including the raise falloff (the index).
      seg[o + C_REACH] = wMax * 0.5 + RIVER_BANK_LIP + bankMax;
      seg[o + C_RREACH] = wMax * 0.5 + RIVER_BANK_LIP + (noRaise ? bankMax : Math.max(RIVER_RAISE_MIN, wMax * RIVER_RAISE_K));
      const dx = seg[o + C_BX] - seg[o + C_AX], dz = seg[o + C_BZ] - seg[o + C_AZ];
      seg[o + C_LEN] = Math.sqrt(dx * dx + dz * dz);
      const reach = seg[o + C_RREACH];
      const x0 = Math.floor((Math.min(seg[o + C_AX], seg[o + C_BX]) - reach) / RIVER_CELL), x1 = Math.floor((Math.max(seg[o + C_AX], seg[o + C_BX]) + reach) / RIVER_CELL);
      const z0 = Math.floor((Math.min(seg[o + C_AZ], seg[o + C_BZ]) - reach) / RIVER_CELL), z1 = Math.floor((Math.max(seg[o + C_AZ], seg[o + C_BZ]) + reach) / RIVER_CELL);
      cellEntries += (x1 - x0 + 1) * (z1 - z0 + 1);
      s++;
    }
  }
  // Pass 2: (key, segment) pairs, sorted by key → CSR.
  const pairKey = new Float64Array(cellEntries);
  const pairSeg = new Int32Array(cellEntries);
  let e = 0;
  for (let i = 0; i < count; i++) {
    const o = i * RIVER_COLS;
    const reach = seg[o + C_RREACH];
    const x0 = Math.floor((Math.min(seg[o + C_AX], seg[o + C_BX]) - reach) / RIVER_CELL), x1 = Math.floor((Math.max(seg[o + C_AX], seg[o + C_BX]) + reach) / RIVER_CELL);
    const z0 = Math.floor((Math.min(seg[o + C_AZ], seg[o + C_BZ]) - reach) / RIVER_CELL), z1 = Math.floor((Math.max(seg[o + C_AZ], seg[o + C_BZ]) + reach) / RIVER_CELL);
    for (let ix = x0; ix <= x1; ix++) for (let iz = z0; iz <= z1; iz++) { pairKey[e] = riverCellKey(ix, iz); pairSeg[e] = i; e++; }
  }
  const order = new Int32Array(cellEntries);
  for (let i = 0; i < cellEntries; i++) order[i] = i;
  // Stable sort by key, then by segment index (deterministic in every thread).
  order.sort((p, q) => (pairKey[p] - pairKey[q]) || (pairSeg[p] - pairSeg[q]));
  let nCells = 0;
  for (let i = 0; i < cellEntries; i++) if (i === 0 || pairKey[order[i]] !== pairKey[order[i - 1]]) nCells++;
  const cellKeys = new Float64Array(nCells);
  const cellStart = new Int32Array(nCells + 1);
  const cellSegs = new Int32Array(cellEntries);
  let c = -1;
  for (let i = 0; i < cellEntries; i++) {
    const k = pairKey[order[i]];
    if (c < 0 || cellKeys[c] !== k) { c++; cellKeys[c] = k; cellStart[c] = i; }
    cellSegs[i] = pairSeg[order[i]];
  }
  cellStart[nCells] = cellEntries;
  riverCache.set(seed, { seg, count, cellKeys, cellStart, cellSegs });
}

/** The registered network as transferable typed arrays (null when none). */
export function riverNetworkTable(seed: string): { seg: Float64Array; count: number; cellKeys: Float64Array; cellStart: Int32Array; cellSegs: Int32Array } | null {
  const n = riverCache.get(seed);
  return n ? { seg: n.seg, count: n.count, cellKeys: n.cellKeys, cellStart: n.cellStart, cellSegs: n.cellSegs } : null;
}

/** Register a network from a table built elsewhere (a worker receiving the main thread's copy). */
export function setRiverNetworkTable(seed: string, t: { seg: Float64Array; count: number; cellKeys: Float64Array; cellStart: Int32Array; cellSegs: Int32Array } | null): void {
  if (!t) { riverCache.delete(seed); return; }
  riverCache.set(seed, { seg: t.seg, count: t.count, cellKeys: t.cellKeys, cellStart: t.cellStart, cellSegs: t.cellSegs });
}

/** Number of registered river segments for a seed (0 = no network). */
export function riverSegmentCount(seed: string): number {
  return riverCache.get(seed)?.count ?? 0;
}

// The riverbed MODIFIER (2026-09-11, "rivers do not apply well with the terrain"):
// the old carve was cut-only (min), so wherever the fine terrain dipped under the
// channel bed — sub-cell noise the 64 m hydrology grid never saw, unfilled pits
// — the ground stayed low and the water ribbon hung in the air (7.9 % of every
// channel metre, gaps to 28 m). A riverbed is an ALPHA-BLEND modifier: inside
// its support the profile REPLACES the ground, raise or cut (Peytavie 2019's
// compactly supported elevation modifiers; Unreal's water bodies "raise or lower
// the terrain so a foundation exists at the height of the water"). Composition:
// channels union by MIN (the deepest bed wins — a bank lip must never shoal its
// neighbour's channel at a polyline joint), bank shelves blend by weight, and a
// reach flagged NORAISE (on a lake, at the sea) only ever cuts, so no river
// builds a bar under another body of water.
let carveTarget = 0;
let carveWeight = 0;
const CARVE_NONE = 0, CARVE_CHANNEL = 1, CARVE_BANK = 2, CARVE_LAKE_FLOOR = 3;

/** One segment's modifier at (x, z) over base height h → carveTarget/carveWeight; returns the kind. */
function riverCarveSeg(seg: Float64Array, o: number, x: number, z: number, h: number): number {
  const ax = seg[o + C_AX], az = seg[o + C_AZ];
  const dx = seg[o + C_BX] - ax, dz = seg[o + C_BZ] - az;
  const px = x - ax, pz = z - az;
  const len2 = dx * dx + dz * dz;
  let t = len2 > 0 ? (px * dx + pz * dz) / len2 : 0;
  if (t < 0) t = 0; else if (t > 1) t = 1;
  const ex = px - dx * t, ez = pz - dz * t;
  const d = Math.sqrt(ex * ex + ez * ez);
  const da = seg[o + C_DA];
  const width = da + (seg[o + C_DB] - da) * t;
  const hw = width * 0.5;
  const bank = Math.max(RIVER_BANK_MIN, width * RIVER_BANK_K);
  const noRaise = seg[o + C_NORAISE] !== 0;
  const raise = noRaise ? bank : Math.max(RIVER_RAISE_MIN, width * RIVER_RAISE_K);
  if (d >= hw + RIVER_BANK_LIP + raise) return CARVE_NONE;
  const pa = seg[o + C_PA];
  const depth = pa + (seg[o + C_PB] - pa) * t;
  const wa = seg[o + C_WA], wb = seg[o + C_WB];
  let water: number;
  if (seg[o + C_FALL] !== 0) {
    // The water holds the upstream level to the lip (t = 0.5) and drops over FALL_LIP_RUN.
    const lip = 0.5;
    const len = seg[o + C_LEN];
    const run = len > 0 ? FALL_LIP_RUN / len : 1;
    const u = t <= lip ? 0 : t >= lip + run ? 1 : (t - lip) / run;
    water = wa + (wb - wa) * (u * u * (3 - 2 * u));
  } else {
    water = wa + (wb - wa) * t;
  }
  const top = water + RIVER_FREEBOARD;
  if (d < hw) {
    // The channel: a parabola from the bed at the centre to the bank top at the edge.
    const u = d / hw;
    const target = water - depth + (depth + RIVER_FREEBOARD) * u * u;
    if (noRaise && target > h) return CARVE_LAKE_FLOOR;
    carveTarget = target; carveWeight = 1;
    return CARVE_CHANNEL;
  }
  // The bank: a flat lip, then a smooth falloff to the natural ground — over the
  // cut shelf when the ground stands above the top, over the wider raise support
  // when it lies below (a reach on still water never raises).
  if (noRaise && top > h) return CARVE_NONE;
  const support = top > h ? raise : bank;
  const dd = d - hw - RIVER_BANK_LIP;
  if (dd >= support) return CARVE_NONE;
  const u = dd <= 0 ? 0 : dd / support;
  carveTarget = top; carveWeight = 1 - u * u * (3 - 2 * u);
  return CARVE_BANK;
}

/** The river layer over a base height (no network ⇒ identity). */
export function riverHeight(seed: string, x: number, z: number, h: number): number {
  const net = riverCache.get(seed);
  if (!net) return h;
  const range = riverCellRange(net, riverCellKey(Math.floor(x / RIVER_CELL), Math.floor(z / RIVER_CELL)));
  if (!range) return h;
  let bed = Infinity;
  let sumW = 0, sumWT = 0;
  let lakeFloor = false;
  for (let i = range[0]; i < range[1]; i++) {
    const kind = riverCarveSeg(net.seg, net.cellSegs[i] * RIVER_COLS, x, z, h);
    if (kind === CARVE_LAKE_FLOOR) lakeFloor = true;   // the floor of a lake a channel crosses: never raised
    else if (kind === CARVE_CHANNEL) { if (carveTarget < bed) bed = carveTarget; }
    else if (kind === CARVE_BANK) { sumW += carveWeight; sumWT += carveWeight * (carveTarget - h); }
  }
  // Channels: the deepest bed, raise or cut — a cut always wins (a neighbouring
  // reach on still water must not cancel it), a raise never lands on a lake floor.
  if (bed !== Infinity) return lakeFloor && bed > h ? h : bed;
  if (sumW > 0) { const b = h + sumWT / (sumW > 1 ? sumW : 1); return lakeFloor && b > h ? h : b; } // banks: weighted blend toward the tops
  return h;
}

/**
 * The water surface (m) of the channel under (x, z) — the nearest reach whose
 * channel (half-width) covers the point — or null off every channel. Used by
 * the regional lake bake (a basin spills through a channel at its WATER level,
 * not its bed), the underwater camera, foliage and route bridges.
 */
export function riverWaterAt(seed: string, x: number, z: number): number | null {
  const net = riverCache.get(seed);
  if (!net) return null;
  const range = riverCellRange(net, riverCellKey(Math.floor(x / RIVER_CELL), Math.floor(z / RIVER_CELL)));
  if (!range) return null;
  let best: number | null = null;
  let bestD = Infinity;
  const seg = net.seg;
  for (let i = range[0]; i < range[1]; i++) {
    const o = net.cellSegs[i] * RIVER_COLS;
    const ax = seg[o + C_AX], az = seg[o + C_AZ];
    const dx = seg[o + C_BX] - ax, dz = seg[o + C_BZ] - az;
    const px = x - ax, pz = z - az;
    const len2 = dx * dx + dz * dz;
    let t = len2 > 0 ? (px * dx + pz * dz) / len2 : 0;
    if (t < 0) t = 0; else if (t > 1) t = 1;
    const ex = px - dx * t, ez = pz - dz * t;
    const d = Math.sqrt(ex * ex + ez * ez);
    const da = seg[o + C_DA];
    const hw = (da + (seg[o + C_DB] - da) * t) * 0.5;
    if (d > hw || d >= bestD) continue;
    bestD = d;
    const wa = seg[o + C_WA], wb = seg[o + C_WB];
    if (seg[o + C_FALL] !== 0) {
      const len = seg[o + C_LEN];
      const run = len > 0 ? FALL_LIP_RUN / len : 1;
      const u = t <= 0.5 ? 0 : t >= 0.5 + run ? 1 : (t - 0.5) / run;
      best = wa + (wb - wa) * (u * u * (3 - 2 * u));
    } else {
      best = wa + (wb - wa) * t;
    }
  }
  return best;
}

/**
 * True when the channel covering (x, z) is a still-water reach (RIVER_FLAG_NORAISE:
 * on a lake at its level, or at the sea) — the regional lake bake treats such a
 * channel as lake floor rather than as a ridge at the river's surface.
 */
export function riverStillAt(seed: string, x: number, z: number): boolean {
  const net = riverCache.get(seed);
  if (!net) return false;
  const range = riverCellRange(net, riverCellKey(Math.floor(x / RIVER_CELL), Math.floor(z / RIVER_CELL)));
  if (!range) return false;
  let bestD = Infinity, still = false;
  const seg = net.seg;
  for (let i = range[0]; i < range[1]; i++) {
    const o = net.cellSegs[i] * RIVER_COLS;
    const ax = seg[o + C_AX], az = seg[o + C_AZ];
    const dx = seg[o + C_BX] - ax, dz = seg[o + C_BZ] - az;
    const px = x - ax, pz = z - az;
    const len2 = dx * dx + dz * dz;
    let t = len2 > 0 ? (px * dx + pz * dz) / len2 : 0;
    if (t < 0) t = 0; else if (t > 1) t = 1;
    const ex = px - dx * t, ez = pz - dz * t;
    const d = Math.sqrt(ex * ex + ez * ez);
    const da = seg[o + C_DA];
    const hw = (da + (seg[o + C_DB] - da) * t) * 0.5;
    if (d > hw || d >= bestD) continue;
    bestD = d; still = seg[o + C_NORAISE] !== 0;
  }
  return still;
}

/** True when any channel's bank zone reaches (x, z) — the cheap "near a river" test. */
export function riverNear(seed: string, x: number, z: number): boolean {
  const net = riverCache.get(seed);
  if (!net) return false;
  const range = riverCellRange(net, riverCellKey(Math.floor(x / RIVER_CELL), Math.floor(z / RIVER_CELL)));
  if (!range) return false;
  const seg = net.seg;
  for (let i = range[0]; i < range[1]; i++) {
    const o = net.cellSegs[i] * RIVER_COLS;
    const ax = seg[o + C_AX], az = seg[o + C_AZ];
    const dx = seg[o + C_BX] - ax, dz = seg[o + C_BZ] - az;
    const px = x - ax, pz = z - az;
    const len2 = dx * dx + dz * dz;
    let t = len2 > 0 ? (px * dx + pz * dz) / len2 : 0;
    if (t < 0) t = 0; else if (t > 1) t = 1;
    const ex = px - dx * t, ez = pz - dz * t;
    const reach = seg[o + C_REACH];
    if (ex * ex + ez * ez < reach * reach) return true;
  }
  return false;
}

/** Terrain height (m) at a world point: base field → rivers → settlement plateaus. */
/**
 * The plateau LEVEL when (x, z) lies inside a settlement's flat disc, else null
 * (SERVER-ONLY read helper, 2026-09-23 — no math of its own, not mirrored).
 * Inside a disc `plateauHeight` returns exactly that disc's `h` whatever the base:
 * the disc weighs 1, every other entry is a ring suppressed to 0 by
 * smooth01(gap ≤ 0) (discs sit ≥ PLATEAU_GAP apart), so the target is h·1/1 and
 * `prodInv` is 0. Uses the same cell list and the same first-64 cap. The AI
 * tick's slope gate skips its terrain samples on a flat disc with this.
 * MIRRORED in shared/terrain.ts since 2026-09-28: the water rules (water_rules.ts) keep a plateau dry on both sides.
 */
export function plateauLevelAt(seed: string, x: number, z: number): number | null {
  const idx = landformIndex(seed);
  if (!idx) return null;
  const list = idx.grid.get(landformCellKey(Math.floor(x / LANDFORM_CELL), Math.floor(z / LANDFORM_CELL)));
  if (!list) return null;
  const n = list.length < 64 ? list.length : 64;
  for (let i = 0; i < n; i++) {
    const s = list[i];
    const dx = x - s.x, dz = z - s.z;
    if (Math.sqrt(dx * dx + dz * dz) <= s.r) return s.h;
  }
  return null;
}

/**
 * The ground WITHOUT the river carve (2026-09-28, the water rules — shared/water_rules.ts ≡ server/src/lib/water_rules.ts).
 * The server never registers the river network, so there `terrainHeight` IS this; the client registers it, so the
 * client asks for this when it must judge a point exactly as the server will (the fishing cast's pond and sea test).
 * The landform order of `landformHeight` with the river layer left out.
 */
export function terrainHeightNoRivers(seed: string, x: number, z: number): number {
  const base = terrainHeightBase(seed, x, z);
  return plateauHeight(seed, x, z, caveApronHeight(seed, x, z, terraceHeight(seed, x, z, base)));
}

export function terrainHeight(seed: string, x: number, z: number): number {
  const base = terrainHeightBase(seed, x, z);
  return landformHeight(seed, x, z, base);
}

// ── Region helpers ────────────────────────────────────────────────────────────────

/** Region index for a world coordinate. */
export function regionOf(coord: number): number {
  return Math.floor(coord / REGION_SIZE_M);
}

// NOTE: `biomeAtRegions` was removed (no remaining callers after Phase-A migration).
// The procedural `biomeAt` / `biomeWeights` are the source of truth; region overrides
// live on the server only and are not used for client-side rendering.
